import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  LayoutDashboard,
  Sun,
  Moon,
  Menu,
  X,
  Zap,
  Cog,
  Puzzle,
  DollarSign,
  BookOpen,
  Webhook,
  Globe,
  Terminal,
  Shield,
  Ruler,
  Users,
  RotateCcw,
  AlertTriangle,
  Code2,
  Newspaper,
  FileText,
  Activity,
  Mail,
  ChevronDown,
  Wrench,
  BookText,
} from 'lucide-react';
import { useEffect, useState, useRef, useCallback } from 'react';
import type { AuthUser as User } from '@linabase/js';
import { BrandLogo } from '@/components/ui/logo';
import { useTheme } from '@/hooks/useTheme';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';

interface MenuItem {
  label: string;
  href: string;
  icon: React.ElementType;
  description: string;
}

interface MenuGroup {
  label: string;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    label: 'Product',
    items: [
      {
        label: 'Features',
        href: '/#features',
        icon: Zap,
        description: 'Real-time forwarding, tunnels, and more',
      },
      {
        label: 'How It Works',
        href: '/#how-it-works',
        icon: Cog,
        description: 'Three steps to your first tunnel',
      },
      {
        label: 'Integrations',
        href: '/integrations',
        icon: Puzzle,
        description: 'Stripe, GitHub, Shopify, and 15+ more',
      },
      {
        label: 'Pricing',
        href: '/pricing',
        icon: DollarSign,
        description: 'Free tier with generous limits',
      },
    ],
  },
  {
    label: 'Docs',
    items: [
      {
        label: 'Getting Started',
        href: '/docs',
        icon: BookOpen,
        description: 'Quick start guide for Hooklink',
      },
      {
        label: 'Endpoints',
        href: '/docs/endpoints',
        icon: Webhook,
        description: 'Create and manage webhook endpoints',
      },
      {
        label: 'Tunnels',
        href: '/docs/tunnels',
        icon: Globe,
        description: 'Expose local servers with HTTPS URLs',
      },
      {
        label: 'CLI Reference',
        href: '/docs/cli',
        icon: Terminal,
        description: 'Commands, flags, and configuration',
      },
      {
        label: 'Security',
        href: '/docs/security',
        icon: Shield,
        description: 'Source allowlists and signature verification',
      },
      {
        label: 'Limits',
        href: '/docs/limits',
        icon: Ruler,
        description: 'Rate limits, quotas, and plan details',
      },
      {
        label: 'Workspaces',
        href: '/docs/workspaces',
        icon: Users,
        description: 'Team collaboration and roles',
      },
      {
        label: 'Replay',
        href: '/docs/replay',
        icon: RotateCcw,
        description: 'Replay and retry failed webhooks',
      },
      {
        label: 'Troubleshooting',
        href: '/docs/troubleshooting',
        icon: AlertTriangle,
        description: 'Common issues and solutions',
      },
      {
        label: 'Glossary',
        href: '/docs/glossary',
        icon: BookText,
        description: 'Webhook and tunnel terminology',
      },
    ],
  },
  {
    label: 'Developers',
    items: [
      {
        label: 'API Reference',
        href: '/docs/api',
        icon: Code2,
        description: 'REST API documentation with examples',
      },
      {
        label: 'Blog',
        href: '/blog',
        icon: Newspaper,
        description: 'Updates, guides, and engineering posts',
      },
      {
        label: 'Changelog',
        href: '/changelog',
        icon: FileText,
        description: 'Latest releases and improvements',
      },
      {
        label: 'Webhook Tester',
        href: '/tools',
        icon: Wrench,
        description: 'Send test webhooks to any URL for free',
      },
    ],
  },
  {
    label: 'Company',
    items: [
      {
        label: 'Status',
        href: '/status',
        icon: Activity,
        description: 'Real-time system health and incidents',
      },
      {
        label: 'Contact',
        href: '/contact',
        icon: Mail,
        description: 'Get in touch with the team',
      },
    ],
  },
];

export function PublicNav() {
  // Synchronous localStorage check for instant auth hint
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(() => {
    try {
      return Object.keys(localStorage).some((k) => k.startsWith('sb-') && k.endsWith('-auth-token'));
    } catch {
      return false;
    }
  });
  const { resolvedTheme, setTheme } = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpandedGroup, setMobileExpandedGroup] = useState<string | null>(
    null
  );
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loading) return;
    let cancelled = false;
    import('@/lib/linabase').then(({ linabase }) => {
      linabase.auth.getSession().then(({ data: { session } }) => {
        if (cancelled) return;
        setUser(session?.user ?? null);
        setLoading(false);
      });
      const subscription = linabase.auth.onAuthStateChange((_event, session) => {
        if (!cancelled) setUser(session?.user ?? null);
      });
      if (!cancelled) {
        cleanupRef.current = () => subscription.unsubscribe();
      } else {
        subscription.unsubscribe();
      }
    });
    const cleanupRef = { current: () => {} };
    return () => {
      cancelled = true;
      cleanupRef.current();
    };
  }, [loading]);

  // Close mobile menu on route change
  /* eslint-disable @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- reset UI on navigation */
  useEffect(() => {
    setMobileOpen(false);
    setMobileExpandedGroup(null);
  }, [location.pathname]);
  /* eslint-enable @eslint-react/hooks-extra/no-direct-set-state-in-use-effect */

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const toggleMobileGroup = useCallback((label: string) => {
    setMobileExpandedGroup((prev) => (prev === label ? null : label));
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-border/50 bg-background/70 backdrop-blur-xl">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between h-16">
          {/* Logo */}
          <BrandLogo size={28} textClassName="font-display text-2xl font-bold tracking-[-0.01em]" />

          {/* Desktop Navigation */}
          <NavigationMenu.Root
            className="hidden lg:flex relative z-10"
            delayDuration={150}
          >
            <NavigationMenu.List className="flex items-center gap-1">
              {menuGroups.map((group) => (
                <NavigationMenu.Item key={group.label}>
                  <NavigationMenu.Trigger className="group flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md data-[state=open]:text-foreground outline-none">
                    {group.label}
                    <ChevronDown className="h-3 w-3 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </NavigationMenu.Trigger>
                  <NavigationMenu.Content className="absolute left-0 top-0 w-auto data-[motion^=from-]:animate-in data-[motion^=from-]:fade-in data-[motion^=to-]:animate-out data-[motion^=to-]:fade-out">
                    <div
                      className={`grid gap-1 p-3 ${group.items.length > 4 ? 'w-[540px] grid-cols-2' : 'w-[340px] grid-cols-1'}`}
                    >
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <NavigationMenu.Link key={item.href} asChild>
                            <Link
                              to={item.href}
                              className="group/item flex items-start gap-3 rounded-lg p-2.5 hover:bg-accent/50 transition-colors outline-none focus:bg-accent/50"
                            >
                              <div className="mt-0.5 h-8 w-8 rounded-md bg-primary/[0.07] border border-primary/10 flex items-center justify-center flex-shrink-0 group-hover/item:bg-primary/10 transition-colors">
                                <Icon className="h-4 w-4 text-primary" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-foreground leading-tight">
                                  {item.label}
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                  {item.description}
                                </div>
                              </div>
                            </Link>
                          </NavigationMenu.Link>
                        );
                      })}
                    </div>
                  </NavigationMenu.Content>
                </NavigationMenu.Item>
              ))}
            </NavigationMenu.List>

            <div className="absolute left-0 top-full flex justify-center perspective-[2000px]">
              <NavigationMenu.Viewport className="relative mt-1.5 origin-top-left overflow-hidden rounded-xl border border-border/60 bg-background/95 backdrop-blur-xl shadow-lg shadow-black/10 data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out h-[var(--radix-navigation-menu-viewport-height)] w-[var(--radix-navigation-menu-viewport-width)] transition-[width,height] duration-200" />
            </div>
          </NavigationMenu.Root>

          {/* Right side: theme toggle, mobile hamburger, auth buttons */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button
              onClick={() =>
                setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
              }
              className="h-8 w-8 rounded-lg border border-border/50 bg-background/50 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all duration-200"
              title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="h-3.5 w-3.5" />
              ) : (
                <Moon className="h-3.5 w-3.5" />
              )}
            </button>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen((prev) => !prev)}
              className="lg:hidden h-8 w-8 rounded-lg border border-border/50 bg-background/50 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all duration-200"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )}
            </button>

            {loading ? (
              <div className="h-9 w-28 shimmer rounded-lg" />
            ) : user ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 hover:shadow-[0_0_20px_hsl(var(--primary)/0.35)] transition-all duration-200"
              >
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden sm:block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 hover:shadow-[0_0_20px_hsl(var(--primary)/0.35)] transition-all duration-200"
                >
                  <span className="sm:hidden">Start Free</span>
                  <span className="hidden sm:inline">Get Started</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      <div
        ref={mobileMenuRef}
        className={`lg:hidden overflow-y-auto border-t border-border/50 bg-background/95 backdrop-blur-xl transition-all duration-300 ease-in-out ${
          mobileOpen
            ? 'max-h-[calc(100vh-4rem)] opacity-100'
            : 'max-h-0 opacity-0 border-t-0'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 py-4 space-y-1">
          {menuGroups.map((group) => (
            <div key={group.label}>
              <button
                onClick={() => toggleMobileGroup(group.label)}
                className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-foreground rounded-lg hover:bg-accent/50 transition-colors"
              >
                {group.label}
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                    mobileExpandedGroup === group.label ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-200 ease-in-out ${
                  mobileExpandedGroup === group.label
                    ? 'max-h-[800px] opacity-100'
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="pl-2 pb-2 space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-accent/50 transition-colors"
                      >
                        <div className="mt-0.5 h-7 w-7 rounded-md bg-primary/[0.07] border border-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground leading-tight">
                            {item.label}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
                            {item.description}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}

          {/* Mobile auth links (visible on small screens where header auth is hidden) */}
          {!loading && !user && (
            <div className="pt-3 mt-2 border-t border-border/50 flex gap-2 sm:hidden">
              <Link
                to="/login"
                className="flex-1 text-center px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground border border-border/60 rounded-lg transition-colors"
              >
                Log in
              </Link>
              <Link
                to="/register"
                className="flex-1 text-center px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg"
              >
                Get Started
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
